import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Order, OrderRefresh, TUserOrder } from '../../@types/Order.type';
import { PAYMENT_METHOD } from '../../@types/Checkout.type';

// All cart checkout payloads are normalized to snake_case before reaching the
// slice. Dispatchers that receive camelCase from mapped endpoints (e.g.
// `changeItemShoppingCart` returning `OrderRefreshMapper`) must convert to
// `OrderRefresh` first via `toSnakeCase` from `shared/utils/caseConverters`.

type PaymentInstructions = {
	show: boolean;
	paymentMethod: PAYMENT_METHOD;
	total: number;
	reference: string;
	referenceLink: string;
	// Unix (checkout: additional_information.expires_at numérico) o ISO string
	// (pending detail normalizado); ModalPaymentInstructions ramifica por typeof.
	expiresAt: number | string;
};

// Atribución de referido hecha desde el input unificado del checkout. Vive en
// el slice (y no en el form) porque el form está al fondo del resumen y la fila
// verde "Referido a ..." se pinta arriba, en el desglose. Solo estado de sesión:
// la atribución real ya quedó persistida server-side vía createReferral.
export type ReferralApplied = {
	code: string;
	referrerName: string | null;
};

export interface CartState {
	numbersOfItems: number;
	orderCheckout: OrderRefresh | null;
	allOrders: OrderRefresh[];
	isLoadingOrderCheckout: boolean;
	orders: Order[];
	paymentInstructions: PaymentInstructions;
	isLoadingOrder?: boolean;
	isLoadingAddToCartV2: boolean;
	// reprice síncrono en vuelo (método/tarjeta recién elegidos): los botones de
	// pagar se deshabilitan para no cobrar un total viejo.
	isRepricingOrder: boolean;
	userOrder: TUserOrder[];
	refreshOrders: OrderRefresh[];
	referralApplied: ReferralApplied | null;
}

const initialState: CartState = {
	numbersOfItems: 0,
	orderCheckout: null,
	allOrders: [],
	isLoadingOrderCheckout: false,
	orders: [],
	refreshOrders: [],
	isLoadingOrder: false,
	paymentInstructions: {
		show: false,
		paymentMethod: PAYMENT_METHOD.SPEI,
		total: 0,
		reference: '',
		referenceLink: '',
		expiresAt: 0,
	},
	isLoadingAddToCartV2: false,
	isRepricingOrder: false,
	userOrder: [],
	referralApplied: null,
};

export const cartSlice = createSlice({
	name: 'Cart',
	initialState,
	reducers: {
		updateAmountOfItems: (state, action: PayloadAction<number>) => {
			state.numbersOfItems = action.payload;
		},
		setAllOrders: (state, action: PayloadAction<OrderRefresh[]>) => {
			state.allOrders = action.payload;
		},
		setIsLoadingOrderCheckout: (state, action: PayloadAction<boolean>) => {
			state.isLoadingOrderCheckout = action.payload;
		},
		setOrders: (state, action: PayloadAction<Order[]>) => {
			state.orders = action.payload;
		},
		setRefreshOrders: (state, action: PayloadAction<OrderRefresh[]>) => {
			state.refreshOrders = action.payload;
		},
		setUserOrder: (state, action: PayloadAction<TUserOrder[]>) => {
			state.userOrder = action.payload;
		},
		setPaymentInstructions: (
			state,
			action: PayloadAction<PaymentInstructions>,
		) => {
			state.paymentInstructions = action.payload;
		},
		clearPaymentInstructions: (state) => {
			state.paymentInstructions = initialState.paymentInstructions;
		},
		setIsLoadingOrder: (state, action: PayloadAction<boolean>) => {
			state.isLoadingOrder = action.payload;
		},
		setOrderCheckout: (
			state,
			action: PayloadAction<OrderRefresh | null>,
		) => {
			state.orderCheckout = action.payload;
		},
		setIsRepricingOrder: (state, action: PayloadAction<boolean>) => {
			state.isRepricingOrder = action.payload;
		},
		setIsLoadingAddToCartV2: (state, action: PayloadAction<boolean>) => {
			state.isLoadingAddToCartV2 = action.payload;
		},
		setReferralApplied: (
			state,
			action: PayloadAction<ReferralApplied | null>,
		) => {
			state.referralApplied = action.payload;
		},
	},
});

export const {
	setIsRepricingOrder,
	updateAmountOfItems,
	setOrders,
	setPaymentInstructions,
	clearPaymentInstructions,
	setIsLoadingOrder,
	setUserOrder,
	setRefreshOrders,
	setOrderCheckout,
	setAllOrders,
	setIsLoadingOrderCheckout,
	setIsLoadingAddToCartV2,
	setReferralApplied,
} = cartSlice.actions;
